import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Trash2, AlertTriangle, Star, DollarSign, CalendarDays, Target, RotateCcw } from 'lucide-react';
import Confetti from 'react-confetti';

export default function VicesPage() {
  const [vices, setVices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showRelapse, setShowRelapse] = useState(null);
  const [showDelete, setShowDelete] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [form, setForm] = useState({ name: '', start_date: new Date().toISOString().split('T')[0], daily_cost: 0, difficulty: '3', goal_days: 30 });

  const fetchVices = useCallback(async () => {
    try {
      const res = await api.get('/vices');
      setVices(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchVices(); }, [fetchVices]);

  const handleAdd = async () => {
    if (!form.name) { toast.error('Informe o nome do hábito'); return; }
    try {
      await api.post('/vices', { ...form, difficulty: parseInt(form.difficulty), daily_cost: parseFloat(form.daily_cost) || 0 });
      toast.success('Hábito registrado! Você está no caminho certo.');
      setShowAdd(false);
      setForm({ name: '', start_date: new Date().toISOString().split('T')[0], daily_cost: 0, difficulty: '3', goal_days: 30 });
      fetchVices();
    } catch (err) { toast.error('Erro ao adicionar'); }
  };

  const handleRelapse = async () => {
    if (!showRelapse) return;
    try {
      await api.post(`/vices/${showRelapse}/relapse`);
      toast.info('Dias difíceis fazem parte. O importante é retomar. Você consegue!');
      setShowRelapse(null);
      fetchVices();
    } catch (err) { toast.error('Erro ao registrar dia difícil'); }
  };

  const handleDelete = async () => {
    if (!showDelete) return;
    try {
      await api.delete(`/vices/${showDelete}`);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4000);
      toast.success('Parabéns! Objetivo alcançado com esse comportamento!');
      setShowDelete(null);
      fetchVices();
    } catch (err) { toast.error('Erro ao remover'); }
  };

  const getDaysClean = (startDate, relapses) => {
    const start = new Date(startDate + 'T00:00:00');
    const today = new Date();
    today.setHours(0,0,0,0);
    return Math.max(0, Math.floor((today - start) / (1000*60*60*24)));
  };

  const getSavings = (vice) => {
    const days = getDaysClean(vice.start_date, vice.relapses);
    return (days * (vice.daily_cost || 0)).toFixed(2);
  };

  const getProgress = (vice) => {
    const days = getDaysClean(vice.start_date, vice.relapses);
    return Math.min(100, Math.round((days / vice.goal_days) * 100));
  };

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-40 bg-muted rounded-2xl animate-pulse" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="vices-page">
      {showConfetti && <Confetti recycle={false} numberOfPieces={300} />}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Hábitos que quero reduzir</h1>
          <p className="text-muted-foreground text-sm mt-1">Acompanhe seu progresso em comportamentos que quer transformar</p>
        </div>
        <Button onClick={() => setShowAdd(true)} className="rounded-full" data-testid="add-vice-btn">
          <Plus className="w-4 h-4 mr-1" /> Adicionar
        </Button>
      </div>

      {vices.length === 0 ? (
        <Card className="py-12">
          <CardContent className="text-center">
            <Target className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-lg font-medium">Pronto para dar o primeiro passo?</p>
            <p className="text-sm text-muted-foreground mt-1">Adicione um comportamento que você quer reduzir ou transformar</p>
            <Button onClick={() => setShowAdd(true)} className="mt-4 rounded-full" data-testid="add-vice-empty-btn">
              <Plus className="w-4 h-4 mr-1" /> Começar minha jornada
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {vices.map((vice) => {
            const days = getDaysClean(vice.start_date, vice.relapses);
            const progress = getProgress(vice);
            const savings = getSavings(vice);
            return (
              <Card key={vice.id} className="hover:shadow-md transition-all" data-testid={`vice-card-${vice.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-heading">{vice.name}</CardTitle>
                      <div className="flex items-center gap-1 mt-1">
                        {[1,2,3,4,5].map(s => (
                          <Star key={s} className={`w-3 h-3 ${s <= vice.difficulty ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground/30'}`} />
                        ))}
                        <span className="text-xs text-muted-foreground ml-1">Dificuldade</span>
                      </div>
                    </div>
                    <Badge variant={progress >= 100 ? 'default' : 'secondary'} className="text-xs">
                      {progress >= 100 ? 'Meta alcançada!' : `${progress}%`}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-center py-3 bg-primary/5 rounded-xl">
                    <p className="text-4xl font-heading font-bold text-primary">{days}</p>
                    <p className="text-sm text-muted-foreground">dias mantendo o plano</p>
                  </div>

                  <div className="w-full bg-muted rounded-full h-2">
                    <div className="bg-primary h-2 rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>

                  <div className="grid grid-cols-3 gap-2 text-center text-xs">
                    <div className="py-2 bg-muted/50 rounded-lg">
                      <CalendarDays className="w-3 h-3 mx-auto mb-1 text-muted-foreground" />
                      <p className="font-medium">Meta: {vice.goal_days}d</p>
                    </div>
                    <div className="py-2 bg-muted/50 rounded-lg">
                      <DollarSign className="w-3 h-3 mx-auto mb-1 text-emerald-500" />
                      <p className="font-medium">R$ {savings}</p>
                    </div>
                    <div className="py-2 bg-muted/50 rounded-lg">
                      <RotateCcw className="w-3 h-3 mx-auto mb-1 text-muted-foreground" />
                      <p className="font-medium">{vice.relapses?.length || 0} dias difíceis</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-xl text-destructive-foreground border-destructive/30 hover:bg-destructive/10"
                      onClick={() => setShowRelapse(vice.id)}
                      data-testid={`relapse-btn-${vice.id}`}
                    >
                      <AlertTriangle className="w-3 h-3 mr-1" /> Dia difícil
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 rounded-xl"
                      onClick={() => setShowDelete(vice.id)}
                      data-testid={`delete-vice-btn-${vice.id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Objetivo alcançado
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Habit to Reduce Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-2xl" data-testid="add-vice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Adicionar hábito para reduzir</DialogTitle>
            <DialogDescription>Registre algo que você quer acompanhar e melhorar ao longo do tempo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input data-testid="vice-name-input" placeholder="Ex: Tempo de tela, açúcar, bebidas..." value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl h-11" />
            </div>
            <div className="space-y-2">
              <Label>Data de início (quando começou a mudança)</Label>
              <Input data-testid="vice-date-input" type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} className="rounded-xl h-11" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Custo diário (R$ opcional)</Label>
                <Input data-testid="vice-cost-input" type="number" step="0.01" value={form.daily_cost} onChange={e => setForm({...form, daily_cost: e.target.value})} className="rounded-xl h-11" />
              </div>
              <div className="space-y-2">
                <Label>Meta (dias)</Label>
                <Input data-testid="vice-goal-input" type="number" value={form.goal_days} onChange={e => setForm({...form, goal_days: parseInt(e.target.value) || 30})} className="rounded-xl h-11" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Dificuldade</Label>
              <Select value={form.difficulty} onValueChange={v => setForm({...form, difficulty: v})}>
                <SelectTrigger className="rounded-xl h-11" data-testid="vice-difficulty-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5].map(d => (
                    <SelectItem key={d} value={String(d)}>{d} - {['Fácil','Moderado','Médio','Difícil','Muito Difícil'][d-1]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-xl">Cancelar</Button>
            <Button onClick={handleAdd} className="rounded-xl" data-testid="save-vice-btn">Adicionar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relapse / Difficult Day Confirmation */}
      <Dialog open={!!showRelapse} onOpenChange={() => setShowRelapse(null)}>
        <DialogContent className="rounded-2xl" data-testid="relapse-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Registrar dia difícil</DialogTitle>
            <DialogDescription>Dias difíceis fazem parte do processo. Quer registrar hoje como um dia fora do plano?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRelapse(null)} className="rounded-xl">Cancelar</Button>
            <Button variant="destructive" onClick={handleRelapse} className="rounded-xl" data-testid="confirm-relapse-btn">Registrar e recomeçar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!showDelete} onOpenChange={() => setShowDelete(null)}>
        <DialogContent className="rounded-2xl" data-testid="delete-vice-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Objetivo alcançado!</DialogTitle>
            <DialogDescription>Parabéns! Marcar este hábito como concluído vai removê-lo da sua lista. Tem certeza?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(null)} className="rounded-xl">Cancelar</Button>
            <Button onClick={handleDelete} className="rounded-xl" data-testid="confirm-delete-vice-btn">Sim, concluído!</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
